import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';

// Tall glass windows in the glass hall's walls, placed in the gaps between paintings.
// Positions are along each wall (metres from the wall's centre).
export const HALL_WINDOWS = {
  north: [-4, 4],
  east: [-4, 4],
  west: [-9.5, 0, 9.5],
};
const WIN = { w: 1.8, h: 3.0, sill: 1.4 };
const STEEL = new THREE.MeshStandardMaterial({ color: '#1d2329', metalness: 0.7, roughness: 0.35 });

// A wall (like the old solid box) with rectangular window openings.
// Local frame: x along the wall, y up, centred on the wall's middle plane.
export const WallWithWindows = ({ length, height, thickness = 0.5, windows, material, onWindowClick, onWindowHover, onWindowHoverOut, ...props }) => {
  const geometry = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-length / 2, 0); s.lineTo(length / 2, 0); s.lineTo(length / 2, height); s.lineTo(-length / 2, height); s.lineTo(-length / 2, 0);
    s.holes = windows.map((x) => {
      const p = new THREE.Path();
      p.moveTo(x - WIN.w / 2, WIN.sill); p.lineTo(x + WIN.w / 2, WIN.sill);
      p.lineTo(x + WIN.w / 2, WIN.sill + WIN.h); p.lineTo(x - WIN.w / 2, WIN.sill + WIN.h); p.lineTo(x - WIN.w / 2, WIN.sill);
      return p;
    });
    const g = new THREE.ExtrudeGeometry(s, { depth: thickness, bevelEnabled: false });
    g.translate(0, 0, -thickness / 2);
    // Same texture scale as the old 24 m box walls (UV 0..1 across the wall).
    const uv = g.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) / length, uv.getY(i) / length);
    return g;
  }, [length, height, thickness, windows]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <group {...props}>
      <mesh geometry={geometry} material={material} castShadow receiveShadow />
      {windows.map((x) => <WindowFrame key={x} x={x} thickness={thickness} onClick={onWindowClick} onHover={onWindowHover} onHoverOut={onWindowHoverOut} />)}
    </group>
  );
};

// Where to stand to look out of a window: 1.5 m inside it at eye height, looking out and a little
// down (towards the lawn and the animals). The glass faces into the hall along its local +z.
const _c = new THREE.Vector3();
const _n = new THREE.Vector3();
const _q = new THREE.Quaternion();
const lookOutWalk = (glass) => {
  glass.getWorldPosition(_c);
  _n.set(0, 0, 1).applyQuaternion(glass.getWorldQuaternion(_q));
  _n.y = 0;
  _n.normalize();
  return {
    position: [_c.x + _n.x * 1.5, 3, _c.z + _n.z * 1.5],
    lookAt: [_c.x - _n.x * 10, 2.1, _c.z - _n.z * 10],
  };
};

// Slim steel frame with glazing bars, a sill, and nearly clear glass (it reflects a little).
const WindowFrame = ({ x, thickness, onClick, onHover, onHoverOut }) => {
  const glass = useMemo(
    () => new THREE.MeshPhysicalMaterial({ color: '#e8f2f4', transparent: true, opacity: 0.1, roughness: 0.02, metalness: 0, envMapIntensity: 1.6, depthWrite: false, side: THREE.DoubleSide }),
    []
  );
  useEffect(() => () => glass.dispose(), [glass]);
  const d = thickness + 0.08;
  const t = 0.07;
  const cy = WIN.sill + WIN.h / 2;
  return (
    <group position={[x, 0, 0]}>
      {/* outer frame */}
      {[-1, 1].map((sgn) => (
        <mesh key={`v${sgn}`} position={[sgn * (WIN.w / 2 - t / 2), cy, 0]} material={STEEL}>
          <boxGeometry args={[t, WIN.h, d]} />
        </mesh>
      ))}
      {[WIN.sill + t / 2, WIN.sill + WIN.h - t / 2].map((y) => (
        <mesh key={`h${y}`} position={[0, y, 0]} material={STEEL}>
          <boxGeometry args={[WIN.w, t, d]} />
        </mesh>
      ))}
      {/* glazing bars: one vertical, two horizontal (six panes) */}
      <mesh position={[0, cy, 0]} material={STEEL}><boxGeometry args={[0.035, WIN.h, 0.05]} /></mesh>
      {[1 / 3, 2 / 3].map((f) => (
        <mesh key={f} position={[0, WIN.sill + WIN.h * f, 0]} material={STEEL}><boxGeometry args={[WIN.w, 0.035, 0.05]} /></mesh>
      ))}
      {/* sills, inside and outside */}
      {[1, -1].map((sgn) => (
        <mesh key={`s${sgn}`} position={[0, WIN.sill - 0.04, sgn * (thickness / 2 + 0.06)]}>
          <boxGeometry args={[WIN.w + 0.25, 0.08, 0.2]} />
          <meshStandardMaterial color="#d9d4c8" roughness={0.6} />
        </mesh>
      ))}
      <mesh
        position={[0, cy, 0]}
        material={glass}
        renderOrder={1}
        onClick={onClick ? (e) => { e.stopPropagation(); onClick(lookOutWalk(e.object)); } : undefined}
        onPointerOver={onHover ? (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; onHover(); } : undefined}
        onPointerOut={onHoverOut ? () => { document.body.style.cursor = ''; onHoverOut(); } : undefined}
      >
        <planeGeometry args={[WIN.w - t, WIN.h - t]} />
      </mesh>
    </group>
  );
};
